import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, UserCog, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Th, Td, Badge, EmptyState } from '../components/ui';
import { ROLE_LABELS } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const ROLES = ['owner', 'admin', 'accountant', 'tailor', 'cutter', 'iron'];
const ROLE_COLOR = {
  owner: 'bg-purple-50 text-purple-700 border-purple-200',
  admin: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  accountant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  tailor: 'bg-amber-50 text-amber-700 border-amber-200',
  cutter: 'bg-cyan-50 text-cyan-700 border-cyan-200',
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [tailors, setTailors] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => {
    api.get('/users').then((r) => setUsers(r.data));
    api.get('/tailors').then((r) => setTailors(r.data));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => { if (!window.confirm('Delete user?')) return; try { await api.delete(`/users/${id}`); toast.success('Deleted'); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader title="Users & Access" subtitle="Manage team members and role permissions"
        actions={<Button onClick={() => setEdit('new')} data-testid="add-user-btn"><Plus size={16} /> User</Button>} />

      <Card className="p-4 mb-6 bg-slate-50/60">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck size={16} className="text-[#1E3A8A]" /><span className="text-sm font-semibold text-slate-700">Role Permissions</span></div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 text-xs">
          {[['Owner', 'Full access · delete'], ['Admin', 'Create & edit · no delete'], ['Sales', 'Inventory, billing & payments'], ['Cutter', 'Inventory & cutting'], ['Tailor', 'Timesheets & production']].map(([r, d]) => (
            <div key={r} className="bg-white border border-slate-200 rounded p-2.5">
              <p className="font-semibold text-slate-800">{r}</p>
              <p className="text-slate-400 mt-0.5">{d}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50/60 transition-colors" data-testid="user-row">
                  <Td className="font-medium text-slate-800">{u.name}</Td>
                  <Td className="text-slate-500">{u.email}</Td>
                  <Td><Badge className={ROLE_COLOR[u.role]}>{ROLE_LABELS[u.role]}</Badge></Td>
                  <Td>{u.active ? <span className="text-emerald-600 text-xs">● Active</span> : <span className="text-slate-400 text-xs">● Inactive</span>}</Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setEdit(u)} className="text-slate-400 hover:text-blue-600 p-1"><Pencil size={15} /></button>
                    {u.id !== user.id && <button onClick={() => del(u.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 size={15} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!users.length && <EmptyState title="No users" icon={UserCog} />}
      </Card>

      {edit && <UserModal user={edit === 'new' ? null : edit} tailors={tailors} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function UserModal({ user, tailors, onClose, onSaved }) {
  const [f, setF] = useState(user ? { ...user, password: '' } : { name: '', email: '', password: '', role: 'admin', phone: '', tailorId: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (user) {
        const payload = { name: f.name, email: f.email, role: f.role, phone: f.phone, active: f.active, tailorId: f.tailorId || null };
        if (f.password) payload.password = f.password;
        await api.put(`/users/${user.id}`, payload);
      } else {
        const isIron = f.role === 'iron';
        await api.post('/users', {
          name: f.name, role: f.role, phone: f.phone, tailorId: f.tailorId || null,
          email: isIron ? (f.email || null) : f.email,
          password: isIron ? null : f.password,
        });
      }
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={user ? 'Edit User' : 'New User'} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-user-btn">Save</Button></>}>
      <div className="space-y-4">
        <Input label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="user-name" />
        <Select label="Role" value={f.role} onChange={(e) => set('role', e.target.value)} data-testid="user-role">
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </Select>
        {f.role !== 'iron' && <Input label="Email" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} data-testid="user-email" />}
        {f.role !== 'iron' && <Input label={user ? 'New Password (leave blank to keep)' : 'Password'} type="password" value={f.password} onChange={(e) => set('password', e.target.value)} data-testid="user-password" />}
        {f.role === 'iron' && <p className="text-xs text-slate-400">Iron staff are salary-only employees — no login, no email or password needed. Pay them from the Salary tab (monthly basis).</p>}
        {f.role === 'tailor' && (
          <Select label="Linked Tailor Profile" value={f.tailorId || ''} onChange={(e) => set('tailorId', e.target.value)}>
            <option value="">— None —</option>
            {tailors.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
        )}
      </div>
    </Modal>
  );
}
